/**
 * statementUtils.js
 * Utility functions for statement processing and formatting
 */

import { format, parseISO, isAfter } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Calculate reconciliation progress for a statement
 * @param {Object} statement - Statement object
 * @returns {number} Progress percentage (0-100)
 */
export const getReconciliationProgress = (statement) => {
  const total = statement.totalTransactions || 0;
  const reconciled = statement.reconciledTransactions || 0;
  return total > 0 ? (reconciled / total) * 100 : 0;
};

/**
 * Format date string to Brazilian Portuguese format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date or fallback message
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'Não informado';
  try {
    const date = parseISO(dateString);
    if (isNaN(date.getTime())) return 'Data inválida';
    return format(date, 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return 'Data inválida';
  }
};

/**
 * Get reconciliation status for a statement
 * @param {Object} statement - Statement object
 * @returns {string} Status: 'completed', 'in-progress', 'pending'
 */
export const getReconciliationStatus = (statement) => {
  const progress = getReconciliationProgress(statement);
  if (progress === 100) return 'completed';
  if (progress > 0) return 'in-progress';
  return 'pending';
};

/**
 * Process statements array safely, handling different API response formats
 * @param {Array|Object} statements - Statements data from API
 * @returns {Array} Processed statements array
 */
export const processStatementsData = (statements) => {
  if (Array.isArray(statements)) {
    return statements;
  } else if (statements && typeof statements === 'object') {
    if (Array.isArray(statements.data)) {
      return statements.data;
    } else {
      console.error('❌ [StatementOverview] Invalid statements format:', statements);
      return [];
    }
  }
  return [];
};

/**
 * Calculate statistics for statement filtering tabs
 * @param {Array} statements - Processed statements array
 * @returns {Object} Statistics object with total, completed, inProgress, pending counts
 */
export const calculateStatementStats = (statements) => {
  const total = statements.length;
  const completed = statements.filter((s) => getReconciliationProgress(s) === 100).length;
  const inProgress = statements.filter((s) => {
    const progress = getReconciliationProgress(s);
    return progress > 0 && progress < 100;
  }).length;
  const pending = statements.filter((s) => getReconciliationProgress(s) === 0).length;

  return { total, completed, inProgress, pending };
};

/**
 * Filter statements based on search term and status filter
 * @param {Array} statements - Statements array
 * @param {string} searchTerm - Search term
 * @param {number} filterTab - Filter tab index (0=all, 1=completed, 2=in-progress, 3=pending)
 * @returns {Array} Filtered statements
 */
export const filterStatements = (statements, searchTerm, filterTab) => {
  let filtered = statements;

  // Apply search filter
  if (searchTerm) {
    filtered = filtered.filter(
      (statement) =>
        statement.fileName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        statement.bankAccount?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  // Apply status filter
  switch (filterTab) {
    case 1: // Concluídos
      filtered = filtered.filter((statement) => getReconciliationProgress(statement) === 100);
      break;
    case 2: // Em andamento
      filtered = filtered.filter((statement) => {
        const progress = getReconciliationProgress(statement);
        return progress > 0 && progress < 100;
      });
      break;
    case 3: // Pendentes
      filtered = filtered.filter((statement) => getReconciliationProgress(statement) === 0);
      break;
    default: // Todos
      break;
  }

  return filtered;
};

/**
 * Sort statements by date
 * @param {Array} statements - Statements array
 * @param {string} sortOrder - 'newest' or 'oldest'
 * @returns {Array} Sorted statements
 */
export const sortStatements = (statements, sortOrder) => {
  return [...statements].sort((a, b) => {
    const dateA = parseISO(a.uploadDate || a.dateCreate || '1970-01-01');
    const dateB = parseISO(b.uploadDate || b.dateCreate || '1970-01-01');

    if (sortOrder === 'newest') {
      return isAfter(dateA, dateB) ? -1 : 1;
    } else {
      return isAfter(dateA, dateB) ? 1 : -1;
    }
  });
};
